import {
  Grid,
  Container,
  Alert,
  Skeleton,
  useTheme,
  Typography,
  Box,
  Stack
} from '@material-ui/core';
import { useQuery, gql } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import usePeriod from '../hooks/usePeriod';
import PeriodSelector from '../components/PeriodSelector';
import { useAccount } from '../hooks/useAccount';
import SingleChart from '../components/SingleChart';
import MetricCard from '../components/MetricCard';
import Page from '../components/Page';
import RecommendationsChart from '../components/RecommendationsChart';
import ContainerMessage from '../components/ContainerMessage';

function divide(a, b) {
  if (b === 0) {
    return 0;
  }
  if (a / b === Infinity) {
    return false;
  }
  return a / b;
}

function calculateStatistics(items) {
  if (items.length < 1) {
    return null;
  }
  const labels = [];
  const uniqueVisitors = [];
  const recommendations = [];
  const recommendedProductViews = [];
  const recoViewsRate = [];
  const addedToCartRates = [];
  const productViews = [];
  const addedToCart = [];
  const recommendedAddedToCart = [];
  const recommendedOrders = [];
  const ordered = [];
  const recoToViewsShare = [];
  const recoAddedToAddedShare = [];
  const recommendedOrderShare = [];

  const recommendedOrderShareTooltip = [];
  const recoViewsRateTooltip = [];
  const recoAddedToAddedShareTooltip = [];

  items.forEach((item) => {
    labels.push(item.dateKey || '-');
    uniqueVisitors.push(item.uniqueVisitors || 0);
    recommendations.push(item.recommendations || 0);
    recommendedProductViews.push(item.recommendedProductViews || 0);
    recoViewsRate.push(divide(item.recommendedProductViews, item.recommendations) || 0);
    addedToCartRates.push(divide(item.recommendedAddedToCart, item.addedToCart) || 0);
    recommendedOrderShare.push(divide(item.recommendedOrders, item.orders) || 0);
    productViews.push(item.productViews || 0);
    recommendedAddedToCart.push(item.recommendedAddedToCart || 0);
    addedToCart.push(item.addedToCart || 0);
    ordered.push(item.orders || 0);
    recommendedOrders.push(item.recommendedOrders || 0);
    recoToViewsShare.push(divide(item.recommendedProductViews, item.productViews) || 0);
    recoAddedToAddedShare.push(divide(item.recommendedAddedToCart, item.addedToCart) || 0);
    recommendedOrderShareTooltip.push(
      `${(divide(item.recommendedOrders, item.orders) * 100).toFixed(0)}% (${
        item.recommendedOrders
      }/${item.orders})`
    );
    recoViewsRateTooltip.push(
      `${(divide(item.recommendedProductViews, item.recommendations) * 100).toFixed(0)}% (${
        item.recommendedProductViews
      }/${item.recommendations})`
    );
    recoAddedToAddedShareTooltip.push(
      `${(divide(item.recommendedAddedToCart, item.addedToCart) * 100).toFixed(0)}% (${
        item.recommendedAddedToCart
      }/${item.addedToCart})`
    );
  });

  const totalOrders = ordered.reduce((prev, curr) => prev + curr, 0);
  const totalAddedToCart = addedToCart.reduce((prev, curr) => prev + curr, 0);
  const totalRecoOrders = recommendedOrders.reduce((prev, curr) => prev + curr, 0);
  const totalRecoAddedToCart = recommendedAddedToCart.reduce((prev, curr) => prev + curr, 0);
  const totalProductViews = productViews.reduce((prev, curr) => prev + curr, 0);
  const totalRecoProductViews = recommendedProductViews.reduce((prev, curr) => prev + curr, 0);

  const totalUniqueVisitors = uniqueVisitors.reduce((prev, curr) => prev + curr, 0);
  const totalRecommendations = recommendations.reduce((prev, curr) => prev + curr, 0);
  const averageRecoViewsRate = divide(totalRecoProductViews, totalProductViews);
  const averageAddedToCartRate = divide(totalRecoAddedToCart, totalAddedToCart);
  const averageRecommendedOrderShare = divide(totalRecoOrders, totalOrders);

  return {
    labels,
    uniqueVisitors,
    recommendations,
    addedToCartRates,
    totalUniqueVisitors,
    totalRecommendations,
    averageRecoViewsRate,
    averageAddedToCartRate,
    averageRecommendedOrderShare,
    productViews,
    addedToCart,
    recommendedAddedToCart,
    ordered,
    recoToViewsShare,
    recoAddedToAddedShare,
    recommendedOrders,
    recommendedOrderShare,
    recommendedOrderShareTooltip,
    recoViewsRateTooltip,
    totalOrders,
    totalAddedToCart,
    totalRecoOrders,
    totalRecoAddedToCart,
    totalProductViews,
    totalRecoProductViews
  };
}

const GET_STATISTICS = gql`
  query listStatistics($domain: ID!, $from: String!, $to: String!) {
    listStatistics(domain: $domain, typeDateKey: { between: [$from, $to] }) {
      items {
        addedToCart
        createdAt
        dateKey
        orders
        productViews
        recommendations
        recommendedAddedToCart
        recommendedOrders
        recommendedProductViews
        type
        typeDateKey
        uniqueVisitors
        updatedAt
      }
    }
  }
`;

export default function OverviewV2() {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    eshop: { domain }
  } = useAccount();
  const { from, to, range, dispatch: dispatchPeriod } = usePeriod();
  const { loading, error, data } = useQuery(GET_STATISTICS, {
    variables: {
      domain,
      from: `daily-${from.clone().format('YYYY-MM-DD')}`,
      to: `daily-${to.clone().format('YYYY-MM-DD')}`
    }
  });
  const statistics = data ? calculateStatistics(data.listStatistics.items) : null;

  return (
    <Page title={t('pages.overview')}>
      <Container>
        <Box>
          <Typography variant="h4" sx={{ mb: 5 }}>
            {t('general.overview')}
          </Typography>
        </Box>
        <Box>
          <PeriodSelector from={from} to={to} range={range} dispatch={dispatchPeriod} />
        </Box>
        {error && <Alert severity="error">{t(`errors.failed_to_load_overview`)}</Alert>}
        {loading && (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} md={9} lg={9} key="skeleton-1">
                <Skeleton
                  height={450}
                  variant="rectangular"
                  sx={{
                    borderRadius: `${theme.shape.borderRadius}px`
                  }}
                />
              </Grid>
              {[...Array(3)].map((_, index) => (
                <Grid item xs={12} md={9} lg={3} key={`skeleton-${index + 1}`}>
                  <Skeleton
                    height={175}
                    variant="rectangular"
                    sx={{
                      borderRadius: `${theme.shape.borderRadius}px`
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {!statistics && (
          <ContainerMessage
            title={t('overview.not_enough_data_title')}
            subtitle={t('overview.not_enough_data_description')}
          />
        )}
        {statistics && (
          <Grid container spacing={3}>
            {/* CHART 1 */}
            <Grid item xs={12} sm={12} md={9} lg={9}>
              {/* Recommendations and visitors chart */}
              <RecommendationsChart
                title={t('overview.recommendations_and_visitors')}
                productViews={statistics.productViews}
                recommendations={statistics.recommendations}
                labels={statistics.labels}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <Stack spacing={3}>
                {/* Number of visitors */}
                <MetricCard
                  title={t('overview.number_of_visitors')}
                  number={statistics.totalUniqueVisitors}
                  tooltip={t('overview.number_of_visitors_tooltip')}
                />
                {/* Number of recommendations */}
                <MetricCard
                  title={t('overview.number_of_recommendations')}
                  number={statistics.totalRecommendations}
                  tooltip={t('overview.number_of_recommendations_tooltip')}
                />
              </Stack>
            </Grid>

            {/* CHART 2 */}
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <Stack spacing={3}>
                {/* Reco Orders */}
                <MetricCard
                  title={t('overview.reco_orders')}
                  number={statistics.totalRecoOrders}
                  tooltip={t('overview.reco_orders_tooltip')}
                />

                {/* Total Orders */}
                <MetricCard
                  title={t('overview.total_orders')}
                  number={statistics.totalOrders}
                  tooltip={t('overview.total_orders_tooltip')}
                />

                {/* Ratio of recommended orders */}
                <MetricCard
                  title={t('overview.ratio_of_recommended_orders')}
                  number={statistics.averageRecommendedOrderShare}
                  isPercentage
                  tooltip={t('overview.ratio_of_recommended_orders_tooltip')}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={12} md={9} lg={9}>
              <SingleChart
                title={t('overview.reco_order_share_title')}
                seriesLabel={t('overview.reco_order_share')}
                dataSeries={statistics.recommendedOrderShare}
                dataTooltip={statistics.recommendedOrderShareTooltip}
                labels={statistics.labels}
                isPercentage
              />
            </Grid>

            {/* CHART 3 */}
            <Grid item xs={12} sm={12} md={9} lg={9}>
              <SingleChart
                title={t('overview.reco_to_views_share_title')}
                seriesLabel={t('overview.reco_to_views_share')}
                dataSeries={statistics.recoToViewsShare}
                dataTooltip={statistics.recoViewsRateTooltip}
                labels={statistics.labels}
                isPercentage
              />
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <Stack spacing={3}>
                {/* Total recommended product views */}
                <MetricCard
                  title={t('overview.total_recommended_product_views')}
                  number={statistics.totalRecoProductViews}
                  tooltip={t('overview.total_recommended_product_views_tooltip')}
                />
                {/* Total product views */}
                <MetricCard
                  title={t('overview.total_product_views')}
                  number={statistics.totalProductViews}
                  tooltip={t('overview.total_product_views_tooltip')}
                />
                {/* CTR */}
                <MetricCard
                  title={t('overview.ctr')}
                  number={statistics.averageRecoViewsRate}
                  isPercentage
                  tooltip={t('overview.ctr_tooltip')}
                />
              </Stack>
            </Grid>

            {/* CHART 4 */}
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <Stack spacing={3}>
                {/* Reco basket total count */}
                <MetricCard
                  title={t('overview.reco_basket_total')}
                  number={statistics.totalRecoAddedToCart}
                  tooltip={t('overview.reco_basket_total_tooltip')}
                />
                {/* Total basket total count */}
                <MetricCard
                  title={t('overview.total_basket_total')}
                  number={statistics.totalAddedToCart}
                  tooltip={t('overview.total_basket_total_tooltip')}
                />
                {/* Basket CTR */}
                <MetricCard
                  title={t('overview.atcr')}
                  number={statistics.averageAddedToCartRate}
                  isPercentage
                  tooltip={t('overview.actr_tooltip')}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={12} md={9} lg={9}>
              <SingleChart
                title={t('overview.reco_added_to_added_share_title')}
                seriesLabel={t('overview.reco_added_to_added_share')}
                dataSeries={statistics.recoAddedToAddedShare}
                dataTooltip={statistics.recoAddedToAddedShareTooltip}
                labels={statistics.labels}
                isPercentage
              />
            </Grid>
          </Grid>
        )}
      </Container>
    </Page>
  );
}
